import { z } from 'zod';

export const RSVP_STATUSES = ['coming', 'maybe', 'not_coming'] as const;
export const RsvpStatusSchema = z.enum(RSVP_STATUSES);
export type RsvpStatus = z.infer<typeof RsvpStatusSchema>;

export const SHIRT_SIZES = ['YXS', 'YS', 'YM', 'YL', 'YXL'] as const;
export const ShirtSizeSchema = z.enum(SHIRT_SIZES);
export type ShirtSize = z.infer<typeof ShirtSizeSchema>;

export const CharacterRowSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  variant: z.string().nullable(),
  image_url: z.string(),
  sort_order: z.number().int(),
  taken: z.union([z.literal(0), z.literal(1)]),
});
export type CharacterRow = z.infer<typeof CharacterRowSchema>;
export const CharactersResponseSchema = z.object({
  characters: z.array(CharacterRowSchema),
});

export const RsvpInputSchema = z
  .object({
    child_name: z.string().trim().min(1).max(80),
    parent_name: z.string().trim().min(1).max(120),
    parent_email: z.string().trim().email().max(254),
    parent_phone: z.string().trim().max(40).nullable().optional(),
    rsvp_status: RsvpStatusSchema,
    character_id: z.number().int().positive().nullable().optional(),
    shirt_size: ShirtSizeSchema.nullable().optional(),
    allergies: z.string().trim().max(1000).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((b) => b.rsvp_status !== 'coming' || b.character_id != null, {
    message: 'character_id required when rsvp_status is coming',
    path: ['character_id'],
  });
export type RsvpInput = z.infer<typeof RsvpInputSchema>;

export const RsvpRowSchema = z.object({
  id: z.number().int(),
  child_name: z.string(),
  parent_name: z.string(),
  parent_email: z.string(),
  parent_phone: z.string().nullable(),
  rsvp_status: RsvpStatusSchema,
  character_id: z.number().int().nullable(),
  character_name: z.string().nullable(),
  character_variant: z.string().nullable(),
  character_image_url: z.string().nullable(),
  shirt_size: ShirtSizeSchema.nullable(),
  allergies: z.string().nullable(),
  notes: z.string().nullable(),
  favor_ordered: z.union([z.literal(0), z.literal(1)]),
  favor_received: z.union([z.literal(0), z.literal(1)]),
  checked_in: z.union([z.literal(0), z.literal(1)]),
  admin_notes: z.string().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
});
export type RsvpRow = z.infer<typeof RsvpRowSchema>;
export const RsvpsResponseSchema = z.object({ rsvps: z.array(RsvpRowSchema) });

export const RsvpSubmitResponseSchema = z.object({
  ok: z.literal(true),
  rsvp: z.object({
    id: z.number().int(),
    rsvp_status: RsvpStatusSchema,
    character_id: z.number().int().nullable(),
  }),
});
export type RsvpSubmitResponse = z.infer<typeof RsvpSubmitResponseSchema>;

export const AdminPatchInputSchema = z.object({
  favor_ordered: z.boolean().optional(),
  favor_received: z.boolean().optional(),
  checked_in: z.boolean().optional(),
  admin_notes: z.string().max(2000).nullable().optional(),
  rsvp_status: RsvpStatusSchema.optional(),
});
export type AdminPatchInput = z.infer<typeof AdminPatchInputSchema>;

export const ApiErrorSchema = z.object({
  error: z.string(),
  issues: z.unknown().optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;
